import { useTranslation } from 'react-i18next';
import { Typography, Box, Divider } from '@mui/material';
import { useFormikContext } from 'formik';
import { PartnersEnum } from 'entities/Partners/enums';
import { ColorPalette } from 'shared/consts/colorPalette';
import { FormSwitch } from 'shared/ui/Switch/FormSwitch';

import { Paper } from 'shared/ui/Paper';
import { InputField } from 'shared/ui/InputField';
import { FormSelect } from 'shared/ui/Select/FormSelect';
import { SelectOption } from 'shared/ui/Select/types';
import { SettingsSchemaType } from '../../validationSchema';

const General = () => {
	const { values, setFieldValue } = useFormikContext<SettingsSchemaType>();
	const { t } = useTranslation();

	return (
		<>
			<Paper
				sx={{
					width: '100%',
					padding: 4,
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'space-between',
				}}
			>
				<Typography variant="body1" color={ColorPalette.astronaut} sx={{ flex: '1 1' }}>
					{t('custom_model.create_edit_modal.label')}
				</Typography>
				<InputField
					name={'name'}
					label={''}
					placeholder={t('custom_model.create_edit_modal.placeholder') as string}
					sx={{ flex: '1 1', margin: '0' }}
					sxError={{ position: 'initial' }}
					required
				/>
			</Paper>

			<Paper sx={{ width: '100%', padding: 4 }}>
				<Typography variant="h5" color={ColorPalette.astronaut}>
					{t('custom_model.settings.email_title')}
				</Typography>

				<Divider color={ColorPalette.periwinkle} sx={{ marginY: 2, borderColor: 'transparent' }} />

				<Box
					sx={{
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'space-between',
						gap: 4,
					}}
				>
					<Typography variant="body1" color={ColorPalette.astronaut} sx={{ flex: '1 1' }}>
						{t('custom_model.settings.email_description')}
					</Typography>

					<FormSwitch name="settings.sendEmailOnPartnerAssociationFailure" />
				</Box>

				<Divider color={ColorPalette.periwinkle} sx={{ marginY: 2, borderColor: 'transparent' }} />

				<Box
					sx={{
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'space-between',
						gap: 4,
					}}
				>
					<Typography variant="body1" color={ColorPalette.astronaut} sx={{ flex: '1 1' }}>
						{t('custom_model.settings.email_receiver_id_not_found_description')}
					</Typography>

					<FormSwitch name="settings.sendEmailOnReceiverIdNotFound" />
				</Box>

				<Divider color={ColorPalette.periwinkle} sx={{ marginY: 2, borderColor: 'transparent' }} />

				<Box
					sx={{
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'space-between',
						gap: 4,
					}}
				>
					<Typography variant="body1" color={ColorPalette.astronaut} sx={{ flex: '1 1' }}>
						{t('custom_model.settings.email_item_code_not_found_description')}
					</Typography>

					<FormSwitch name="settings.sendEmailOnItemCodeNotFound" />
				</Box>

				<Divider color={ColorPalette.periwinkle} sx={{ marginY: 2, borderColor: 'transparent' }} />

				<Box
					sx={{
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'space-between',
						gap: 4,
					}}
				>
					<Typography variant="body1" color={ColorPalette.astronaut} sx={{ flex: '1 1' }}>
						{t('custom_model.settings.email_success_export_description')}
					</Typography>

					<FormSwitch name="settings.sendEmailOnSuccessExport" />
				</Box>
			</Paper>

			<Paper sx={{ width: '100%', padding: 4 }}>
				<Typography variant="h5" color={ColorPalette.astronaut}>
					{t('custom_model.settings.split_title')}
				</Typography>

				<Divider color={ColorPalette.periwinkle} sx={{ marginY: 2, borderColor: 'transparent' }} />

				<Box
					sx={{
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'space-between',
						gap: 4,
					}}
				>
					<Typography variant="body1" color={ColorPalette.astronaut} sx={{ flex: '1 1' }}>
						{t('custom_model.settings.split_description')}
					</Typography>

					<FormSwitch name="settings.splitUploadedDoc" />
				</Box>
			</Paper>

			<Paper sx={{ width: '100%', padding: 4 }}>
				<Typography variant="h5" color={ColorPalette.astronaut}>
					{t('custom_model.settings.partner_title')}
				</Typography>

				<Divider color={ColorPalette.periwinkle} sx={{ marginY: 2, borderColor: 'transparent' }} />

				<Box
					sx={{
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'space-between',
						gap: 4,
					}}
				>
					<Typography variant="body1" color={ColorPalette.astronaut} sx={{ flex: '1 1' }}>
						{t('custom_model.settings.partner_description')}
					</Typography>
					<FormSelect
						name={'settings.partnerType'}
						label={''}
						placeholder={t('partners.placeholder.client_supplier')}
						options={selectOptions}
						value={values.settings.partnerType}
						onSelect={(value) => setFieldValue('settings.partnerType', value)}
						sx={{ flex: '1 1', marginBottom: 0 }}
						sxError={{ margin: 0 }}
					/>
				</Box>
			</Paper>

			<Paper sx={{ width: '100%', padding: 4 }}>
				<Typography variant="h5" color={ColorPalette.astronaut}>
					{t('files.default_import.process')}
				</Typography>

				<Divider color={ColorPalette.periwinkle} sx={{ marginY: 2, borderColor: 'transparent' }} />

				<Box
					sx={{
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'space-between',
						gap: 4,
					}}
				>
					<Typography variant="body1" color={ColorPalette.astronaut} sx={{ flex: '1 1' }}>
						{t('files.default_import.description_2')}
					</Typography>
					<InputField
						label={''}
						name="settings.pagesRange"
						placeholder={'Example: 1-3 , 5'}
						sx={{ flex: '1 1', marginBottom: 0 }}
					/>
				</Box>
			</Paper>
		</>
	);
};

export default General;

const selectOptions: Array<SelectOption> = [
	{
		value: undefined,
		label: '-',
	},
	{
		value: PartnersEnum.client,
		label: 'partners.tabs.clients',
	},
	{
		value: PartnersEnum.supplier,
		label: 'partners.tabs.suppliers',
	},
];
